import api from '../lib/api';
import axios from 'axios';
import type {
  SurveyQuestionnaire,
  SurveyQuestionnaireDetail,
  SurveyPublicQuestionnaire,
  SurveyQuestion,
  SurveyOption,
  SurveyResponse,
  SurveyResponsePaginated,
  SurveyResponseDetail,
  SurveyChartQuestion,
  QuestionnaireFormData,
  QuestionFormData,
  OptionFormData,
  SurveySubmitRequest,
} from '../types/survey';

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
});

export const surveyService = {
  // Questionnaires (authenticated)
  list: async (): Promise<SurveyQuestionnaire[]> => {
    const res = await api.get<SurveyQuestionnaire[]>('/surveys');
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  },

  getById: async (id: number): Promise<SurveyQuestionnaireDetail> => {
    const res = await api.get<SurveyQuestionnaireDetail>(`/surveys/${id}`);
    return res.data;
  },

  create: async (data: QuestionnaireFormData): Promise<SurveyQuestionnaire> => {
    const res = await api.post<SurveyQuestionnaire>('/surveys', data);
    return res.data;
  },

  update: async (id: number, data: QuestionnaireFormData): Promise<SurveyQuestionnaire> => {
    const res = await api.put<SurveyQuestionnaire>(`/surveys/${id}`, data);
    return res.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/surveys/${id}`);
  },

  // Questions (authenticated)
  listQuestions: async (questionnaireId: number): Promise<SurveyQuestion[]> => {
    const res = await api.get<SurveyQuestion[]>(`/surveys/${questionnaireId}/questions`);
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  },

  createQuestion: async (questionnaireId: number, data: QuestionFormData): Promise<SurveyQuestion> => {
    const res = await api.post<SurveyQuestion>(`/surveys/${questionnaireId}/questions`, data);
    return res.data;
  },

  updateQuestion: async (id: number, data: QuestionFormData): Promise<SurveyQuestion> => {
    const res = await api.put<SurveyQuestion>(`/surveys/questions/${id}`, data);
    return res.data;
  },

  deleteQuestion: async (id: number): Promise<void> => {
    await api.delete(`/surveys/questions/${id}`);
  },

  // Options (authenticated)
  listOptions: async (questionId: number): Promise<SurveyOption[]> => {
    const res = await api.get<SurveyOption[]>(`/surveys/questions/${questionId}/options`);
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  },

  createOption: async (questionId: number, data: OptionFormData): Promise<SurveyOption> => {
    const res = await api.post<SurveyOption>(`/surveys/questions/${questionId}/options`, data);
    return res.data;
  },

  updateOption: async (id: number, data: OptionFormData): Promise<SurveyOption> => {
    const res = await api.put<SurveyOption>(`/surveys/options/${id}`, data);
    return res.data;
  },

  deleteOption: async (id: number): Promise<void> => {
    await api.delete(`/surveys/options/${id}`);
  },

  // Responses (authenticated)
  listResponses: async (
    questionnaireId: number,
    filters?: {
      date_from?: string;
      date_to?: string;
      option_id?: number;
      user_account_id?: number;
      page?: number;
      limit?: number;
    }
  ): Promise<SurveyResponsePaginated> => {
    const params: Record<string, string | number> = {};
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    if (filters?.option_id) params.option_id = filters.option_id;
    if (filters?.user_account_id != null) params.user_account_id = filters.user_account_id;
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    const res = await api.get<SurveyResponsePaginated>(`/surveys/${questionnaireId}/responses`, { params });
    return res.data;
  },

  listResponsesForChart: async (
    questionnaireId: number,
    filters?: {
      date_from?: string;
      date_to?: string;
      option_id?: number;
      user_account_id?: number;
      assistant_id?: number;
      whatsapp_id?: number;
      contact_id?: number;
    }
  ): Promise<SurveyChartQuestion[]> => {
    const params: Record<string, string | number> = {};
    if (filters?.date_from) params.date_from = filters.date_from;
    if (filters?.date_to) params.date_to = filters.date_to;
    if (filters?.option_id) params.option_id = filters.option_id;
    if (filters?.user_account_id != null) params.user_account_id = filters.user_account_id;
    if (filters?.assistant_id) params.assistant_id = filters.assistant_id;
    if (filters?.whatsapp_id) params.whatsapp_id = filters.whatsapp_id;
    if (filters?.contact_id) params.contact_id = filters.contact_id;
    const res = await api.get<SurveyChartQuestion[]>(`/surveys/${questionnaireId}/responses/chart`, { params });
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  },

  getResponse: async (id: number): Promise<SurveyResponseDetail> => {
    const res = await api.get<SurveyResponseDetail>(`/surveys/responses/${id}`);
    return res.data;
  },

  // Public (no auth)
  getPublic: async (id: number, ticketId?: number): Promise<SurveyPublicQuestionnaire> => {
    const params: Record<string, number> = {};
    if (ticketId) params.ticket_id = ticketId;
    const res = await publicApi.get<SurveyPublicQuestionnaire>(`/surveys/${id}/public`, { params });
    return res.data;
  },

  submit: async (id: number, data: SurveySubmitRequest): Promise<{ message: string; response_id: number }> => {
    const res = await publicApi.post<{ message: string; response_id: number }>(`/surveys/${id}/responses`, data);
    return res.data;
  },
};
